import { useCallback, useEffect, useMemo, useState } from "react";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { HeroInlineLogo } from "@/components/dataTable";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  formatHours,
  getEmployeeYearSummary,
  type EmployeeTimeYearSummary,
} from "@/lib/employeeTimes";
import {
  expandEffectiveAbsenceDays,
  listAbsenceRequests,
  type AbsenceDayMarker,
  type EmployeeAbsenceRequest,
} from "@/lib/employeeAbsences";
import {
  calendarDaysToMap,
  getCalendarDays,
  type EmployeeCalendarDay,
} from "@/lib/employeeCalendar";
import {
  EmployeeLockedAutocomplete,
  employeeLoginToOption,
  type EmployeeOption,
} from "@/pages/admin/zeiterfassung/EmployeeLockedAutocomplete";
import {
  expandHeroAbsenceDays,
  getHeroEmployeeCalendar,
  heroHoursByDayMap,
} from "@/lib/heroEmployeeHr";
import { AbsenceCalendarLegend } from "@/pages/admin/zeiterfassung/AbsenceCalendarLegend";
import { useRefreshKeyLoad } from "@/pages/admin/zeiterfassung/useRefreshKeyLoad";
import { YearDayDetailDialog } from "@/pages/admin/zeiterfassung/YearDayDetailDialog";
import { YearOverviewCell } from "@/pages/admin/zeiterfassung/YearOverviewCell";
import {
  buildYearGrid,
  MONTH_LONG,
  todayIso,
  WEEKDAY_SHORT,
  type YearGridCell,
} from "@/pages/admin/zeiterfassung/yearOverviewUtils";
import "./yearOverview.css";
import { TimesExportButton } from "@/pages/admin/zeiterfassung/TimesExportButton";

interface YearOverviewPanelProps {
  refreshKey: number;
  defaultEmployeeLogin?: string;
  defaultEmployeeDisplay?: string;
  allowEmployeePicker?: boolean;
  includeHeroOverlay?: boolean;
}

export function YearOverviewPanel({
  refreshKey,
  defaultEmployeeLogin,
  defaultEmployeeDisplay,
  allowEmployeePicker = true,
  includeHeroOverlay = true,
}: YearOverviewPanelProps) {
  const now = new Date();
  const [selectedEmployee, setSelectedEmployee] = useState<AutocompleteOption<EmployeeOption> | null>(
    () => employeeLoginToOption(defaultEmployeeLogin || "", defaultEmployeeDisplay),
  );
  const employeeLogin = selectedEmployee?.value.login ?? "";
  const employeeDisplay = selectedEmployee?.value.display ?? "";
  const [year, setYear] = useState(now.getFullYear());
  const [summary, setSummary] = useState<EmployeeTimeYearSummary | null>(null);
  const [absences, setAbsences] = useState<EmployeeAbsenceRequest[]>([]);
  const [calendarByDay, setCalendarByDay] = useState<Map<string, EmployeeCalendarDay>>(new Map());
  const [heroHoursByDay, setHeroHoursByDay] = useState<Map<string, number>>(new Map());
  const [heroAbsenceByDay, setHeroAbsenceByDay] = useState<Map<string, AbsenceDayMarker[]>>(new Map());
  const [heroLinked, setHeroLinked] = useState(false);
  const [heroPartnerName, setHeroPartnerName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCell, setSelectedCell] = useState<YearGridCell | null>(null);

  useEffect(() => {
    if (!allowEmployeePicker && defaultEmployeeLogin) {
      setSelectedEmployee(
        employeeLoginToOption(defaultEmployeeLogin, defaultEmployeeDisplay),
      );
    }
  }, [allowEmployeePicker, defaultEmployeeLogin, defaultEmployeeDisplay]);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!employeeLogin) {
      setSummary(null);
      setAbsences([]);
      setCalendarByDay(new Map());
      setHeroHoursByDay(new Map());
      setHeroAbsenceByDay(new Map());
      setHeroLinked(false);
      setHeroPartnerName(null);
      return;
    }
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const heroPromise = includeHeroOverlay
        ? getHeroEmployeeCalendar({ year, user_login: employeeLogin })
        : Promise.resolve(null);
      const [data, absenceRows, calendar, heroData] = await Promise.all([
        getEmployeeYearSummary({ user_login: employeeLogin, year }),
        listAbsenceRequests({ user_login: employeeLogin, year }),
        getCalendarDays({ user_login: employeeLogin, year }),
        heroPromise,
      ]);
      setSummary(data);
      setAbsences(absenceRows);
      setCalendarByDay(calendarDaysToMap(calendar.days));
      if (heroData) {
        setHeroHoursByDay(heroHoursByDayMap(heroData.hours_by_day));
        setHeroAbsenceByDay(expandHeroAbsenceDays(heroData.absences));
        setHeroLinked(heroData.linked);
        setHeroPartnerName(heroData.partner_name);
      } else {
        setHeroHoursByDay(new Map());
        setHeroAbsenceByDay(new Map());
        setHeroLinked(false);
        setHeroPartnerName(null);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Jahresübersicht konnte nicht geladen werden.");
      setSummary(null);
      setAbsences([]);
      setCalendarByDay(new Map());
      setHeroHoursByDay(new Map());
      setHeroAbsenceByDay(new Map());
      setHeroLinked(false);
      setHeroPartnerName(null);
    } finally {
      setLoading(false);
    }
  }, [employeeLogin, year, includeHeroOverlay]);

  useRefreshKeyLoad(load, refreshKey);

  const absenceByDay = useMemo(
    () => expandEffectiveAbsenceDays(absences, calendarByDay, { year, includePending: true }),
    [absences, calendarByDay, year],
  );

  const grid = useMemo(
    () =>
      summary
        ? buildYearGrid(
            summary,
            absenceByDay,
            calendarByDay,
            year,
            includeHeroOverlay
              ? { hoursByDay: heroHoursByDay, absenceByDay: heroAbsenceByDay }
              : undefined,
          )
        : null,
    [summary, absenceByDay, calendarByDay, year, includeHeroOverlay, heroHoursByDay, heroAbsenceByDay],
  );

  const today = todayIso();
  const currentMonthKey = `${now.getFullYear()}-${now.getMonth() + 1}`;

  const avgHoursPerWorkDay =
    grid && grid.workDayCount > 0 ? grid.yearTotal / grid.workDayCount : 0;
  // Überstundenkonto und Differenz basieren auf App-Soll (Arbeitszeitmodell).
  // Hero-Soll nur als Vergleichshinweis – sonst widerspricht die Kachel dem Kalender.
  const yearAppSollHours = Number(summary?.app_expected_hours ?? summary?.expected_hours ?? 0);
  const yearHeroSollHours =
    summary?.hero_expected_hours != null ? Number(summary.hero_expected_hours) : null;
  const yearSollHours = yearAppSollHours;
  const yearIstDelta = grid ? grid.yearTotal - yearSollHours : 0;
  const hasSoll = summary?.has_soll !== false;

  const yearOptions = [
    now.getFullYear() - 2,
    now.getFullYear() - 1,
    now.getFullYear(),
    now.getFullYear() + 1,
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 rounded-ios border border-separator bg-card p-4 shadow-ios md:grid-cols-3">
        {allowEmployeePicker ? (
          <EmployeeLockedAutocomplete
            value={selectedEmployee}
            onChange={setSelectedEmployee}
          />
        ) : (
          <label className="space-y-1">
            <span className="eh-label">Mitarbeiter</span>
            <div className="eh-input flex items-center bg-fill-tertiary/40 text-label">
              {employeeDisplay || employeeLogin}
            </div>
          </label>
        )}
        <label className="space-y-1">
          <span className="eh-label">Jahr</span>
          <select
            className="eh-input w-full"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap items-end gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={!employeeLogin}>
            Laden
          </Button>
          <TimesExportButton
            employeeLogin={employeeLogin}
            year={year}
            includeHero={includeHeroOverlay}
            disabled={!employeeLogin}
          />
        </div>
      </div>

      {!employeeLogin && allowEmployeePicker && (
        <div className="rounded-ios border border-dashed border-separator bg-card p-8 text-center text-sm text-label-secondary">
          Bitte einen Mitarbeiter auswählen, um die Jahresübersicht anzuzeigen.
        </div>
      )}

      {error && <Alert variant="error" title="Fehler">{error}</Alert>}

      {employeeLogin && loading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}

      {employeeLogin && !loading && summary && grid && (
        <>
          <div className="year-overview-header">
            <div>
              <div className="year-overview-header__title">
                {employeeDisplay || employeeLogin}
              </div>
              <div className="year-overview-header__subtitle">
                Jahresübersicht {year}
                {includeHeroOverlay && heroLinked && heroPartnerName
                  ? ` · Hero: ${heroPartnerName}`
                  : includeHeroOverlay && !heroLinked
                    ? " · Hero nicht verknüpft"
                    : ""}
              </div>
            </div>
          </div>

          {hasSoll &&
            !(
              summary.overtime_balance_by_day &&
              Object.keys(summary.overtime_balance_by_day).length > 0
            ) && (
            <Alert variant="info" title="Aktuell +/- nicht verfügbar">
              Für den laufenden Überstundenstand im Kalender wird ein App-Startstand
              benötigt (Mitarbeiter → Überstunden-Startstand). Hero liefert nur noch
              Tagesarbeitszeiten; die Überstundenrechnung erfolgt ausschließlich in der App.
            </Alert>
          )}
          {hasSoll &&
            summary.overtime_balance_by_day &&
            Object.keys(summary.overtime_balance_by_day).length > 0 && (
              <p className="text-xs text-label-secondary">
                Unter den Stunden: laufender Überstundenstand ab App-Startstand (Ist − App-Soll −
                Auszahlungen). Die Differenz-Kachel ist dasselbe ohne Startstand. Zukünftige Tage
                sind prognostiziert (Soll ohne Ist).
              </p>
            )}
          {!hasSoll && (
            <p className="text-xs text-label-secondary">
              Nachunternehmer: erfasste Ist-Stunden ohne Soll und ohne Überstundenkonto.
            </p>
          )}

          <div className="year-overview-stats">
            {hasSoll && (
              <div className="year-overview-stat">
                <div className="year-overview-stat__label">Soll</div>
                <div className="year-overview-stat__value">{formatHours(yearSollHours)}</div>
                {yearHeroSollHours != null && yearHeroSollHours > 0 ? (
                  <div className="year-overview-stat__hint">
                    App (Modell) · Hero {formatHours(yearHeroSollHours)}
                  </div>
                ) : (
                  <div className="year-overview-stat__hint">Arbeitszeitmodell (abzgl. FT/Abw.)</div>
                )}
              </div>
            )}
            <div className="year-overview-stat">
              <div className="year-overview-stat__label">Ist</div>
              <div className="year-overview-stat__value year-overview-stat__value--accent">
                {formatHours(grid.yearTotal)}
              </div>
              {includeHeroOverlay && grid.yearHeroTotal > 0 ? (
                <div className="year-overview-stat__hint">
                  App {formatHours(grid.yearAppTotal)} · Hero {formatHours(grid.yearHeroTotal)}
                </div>
              ) : (
                <div className="year-overview-stat__hint">Erfasste Arbeitszeit</div>
              )}
            </div>
            {hasSoll && yearSollHours > 0 && (
              <div className="year-overview-stat">
                <div className="year-overview-stat__label">Differenz</div>
                <div
                  className={[
                    "year-overview-stat__value",
                    yearIstDelta >= 0 ? "year-overview-stat__value--positive" : "year-overview-stat__value--negative",
                  ].join(" ")}
                >
                  {formatHours(yearIstDelta)}
                </div>
                <div className="year-overview-stat__hint">Ist minus App-Soll</div>
              </div>
            )}
            <div className="year-overview-stat">
              <div className="year-overview-stat__label">Arbeitstage</div>
              <div className="year-overview-stat__value">{grid.workDayCount}</div>
              <div className="year-overview-stat__hint">Tage mit erfassten Stunden</div>
            </div>
            <div className="year-overview-stat">
              <div className="year-overview-stat__label">Abwesenheitstage</div>
              <div className="year-overview-stat__value">{grid.absenceDayCount}</div>
              <div className="year-overview-stat__hint">Genehmigt &amp; ausstehend</div>
            </div>
            {grid.yearPayoutTotal > 0 && (
              <div className="year-overview-stat">
                <div className="year-overview-stat__label">Ausgezahlt</div>
                <div className="year-overview-stat__value">{formatHours(grid.yearPayoutTotal)}</div>
                <div className="year-overview-stat__hint">Überstunden {year}</div>
              </div>
            )}
            <div className="year-overview-stat">
              <div className="year-overview-stat__label">Ø pro Arbeitstag</div>
              <div className="year-overview-stat__value">
                {grid.workDayCount > 0 ? formatHours(avgHoursPerWorkDay) : "—"}
              </div>
            </div>
          </div>

          <div className="year-overview-months">
            {grid.months.map((monthGrid) => {
              const isCurrentMonth = `${year}-${monthGrid.month}` === currentMonthKey;
              const hasAppHours = monthGrid.appTotal > 0;
              const hasHeroHours = includeHeroOverlay && monthGrid.heroTotal > 0;
              const showSourceSplit = hasAppHours && hasHeroHours;
              const showHeroOnTotal = hasHeroHours && !hasAppHours;
              return (
                <section
                  key={monthGrid.month}
                  className={[
                    "year-month-card",
                    isCurrentMonth ? "year-month-card--current" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <header className="year-month-card__header">
                    <h3 className="year-month-card__name">{MONTH_LONG[monthGrid.month - 1]}</h3>
                    <div className="year-month-card__totals">
                      <span
                        className={[
                          "year-month-card__total",
                          monthGrid.total > 0 ? "year-month-card__total--active" : "",
                          showHeroOnTotal ? "year-month-card__total--with-hero" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {showHeroOnTotal ? <HeroInlineLogo /> : null}
                        {monthGrid.total > 0 ? formatHours(monthGrid.total) : "—"}
                      </span>
                      {showSourceSplit ? (
                        <span className="year-month-card__hero-hint">
                          <span>{formatHours(monthGrid.appTotal)}</span>
                          <span className="year-month-card__hero-split">+</span>
                          <span className="inline-flex items-center gap-0.5">
                            <HeroInlineLogo />
                            {formatHours(monthGrid.heroTotal)}
                          </span>
                        </span>
                      ) : null}
                    </div>
                  </header>
                  <div className="year-month-card__weekdays" aria-hidden>
                    {WEEKDAY_SHORT.map((d) => (
                      <span key={d}>{d}</span>
                    ))}
                  </div>
                  <div className="year-month-card__grid">
                    {monthGrid.cells.map((cell, idx) =>
                      cell ? (
                        <YearOverviewCell
                          key={cell.iso}
                          cell={cell}
                          isToday={cell.iso === today}
                          onSelect={setSelectedCell}
                        />
                      ) : (
                        <span key={`blank-${idx}`} className="year-day year-day--blank" aria-hidden />
                      ),
                    )}
                  </div>
                </section>
              );
            })}
          </div>

          <div className="year-overview-legend">
        <AbsenceCalendarLegend showHeroLegend={includeHeroOverlay && heroLinked} />
      </div>
        </>
      )}

      <YearDayDetailDialog
        cell={selectedCell}
        employeeLogin={employeeLogin}
        employeeDisplay={employeeDisplay}
        onClose={() => setSelectedCell(null)}
      />
    </div>
  );
}
